import { Injectable } from '@angular/core';
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
import * as QRCode from 'qrcode';

const ARABIC_FONT = '"Noto Naskh Arabic", "Segoe UI", "Tahoma", "Geeza Pro", Arial, sans-serif';
/** A4 page — HTML pixels map 1:1 to PDF millimetres. */
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PAGE_WIDTH_PX = 794;
const PX_PER_MM = PAGE_WIDTH_PX / A4_WIDTH_MM;
const PAGE_HEIGHT_PX = Math.round(A4_HEIGHT_MM * PX_PER_MM);
/**
 * ISO/IEC 7810 ID-1 (Visa / CR80) in portrait for student badges.
 * Landscape ID-1 is 85.60 × 53.98 mm.
 */
const CARD_WIDTH_MM = 53.98;
const CARD_HEIGHT_MM = 85.6;
const CARD_CORNER_RADIUS_MM = 3.18;
const CARD_WIDTH_PX = Math.round(CARD_WIDTH_MM * PX_PER_MM);
const CARD_HEIGHT_PX = Math.round(CARD_HEIGHT_MM * PX_PER_MM);
const CARD_RADIUS_PX = Math.round(CARD_CORNER_RADIUS_MM * PX_PER_MM);
/** Outer page margin for cutting / lamination handling. */
const PAGE_MARGIN_MM = 12;
const PAGE_MARGIN_PX = Math.round(PAGE_MARGIN_MM * PX_PER_MM);
const GRID_COLS = 3;
const GRID_ROWS = 3;
const CARDS_PER_PAGE = GRID_COLS * GRID_ROWS;
const USABLE_WIDTH_MM = A4_WIDTH_MM - PAGE_MARGIN_MM * 2;
const USABLE_HEIGHT_MM = A4_HEIGHT_MM - PAGE_MARGIN_MM * 2;
const GAP_X_MM = (USABLE_WIDTH_MM - GRID_COLS * CARD_WIDTH_MM) / (GRID_COLS - 1);
const GAP_Y_MM = (USABLE_HEIGHT_MM - GRID_ROWS * CARD_HEIGHT_MM) / (GRID_ROWS - 1);
const GAP_X_PX = Math.round(GAP_X_MM * PX_PER_MM);
const GAP_Y_PX = Math.round(GAP_Y_MM * PX_PER_MM);
/** Default cut margin around each card (overridden from school settings). */
const DEFAULT_CUT_MARGIN_MM = 3.5;
/** Crop-mark size for paper-edge cut guides. */
const MARK_THICK_PX = Math.max(1, Math.round(0.25 * PX_PER_MM));
const EDGE_MARK_LEN_PX = Math.max(8, PAGE_MARGIN_PX - Math.round(2 * PX_PER_MM));
const EDGE_MARK_INSET_PX = Math.max(2, Math.round(1 * PX_PER_MM));
const CUT_MARK = '#344054';
const INK = '#173654';
const MUTED = '#5b6675';
const LINE = '#d0d5dd';
const ACCENT = '#0f766e';

export interface StudentCardPdfItem {
  id: string;
  nameAr: string;
  nameEn: string;
  className: string;
  busText: string;
  qrCode: string;
}

export interface StudentCardPdfLabels {
  title: string;
  classLabel: string;
  noClass: string;
  noQr: string;
}

export interface StudentCardPdfExportInput {
  schoolName: string;
  isArabic: boolean;
  labels: StudentCardPdfLabels;
  students: StudentCardPdfItem[];
  cardColors?: {
    busBackground?: string;
    noBusBackground?: string;
    cutMarginMm?: number;
  };
}

const DEFAULT_CARD_BUS_BG = '#e8f3ff';
const DEFAULT_CARD_NO_BUS_BG = '#fff7ed';

function normalizeHexColor(value: string | undefined, fallback: string): string {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) {
    return raw.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

function darkenHex(hex: string, factor = 0.18): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.round(((n >> 16) & 255) * (1 - factor)));
  const g = Math.max(0, Math.round(((n >> 8) & 255) * (1 - factor)));
  const b = Math.max(0, Math.round((n & 255) * (1 - factor)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const SHEET_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
  }
  #sheet {
    position: relative;
    width: ${PAGE_WIDTH_PX}px;
    height: ${PAGE_HEIGHT_PX}px;
    padding: ${PAGE_MARGIN_PX}px;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
    overflow: hidden;
  }
  .cards-grid {
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns: repeat(${GRID_COLS}, ${CARD_WIDTH_PX}px);
    grid-template-rows: repeat(${GRID_ROWS}, ${CARD_HEIGHT_PX}px);
    column-gap: ${GAP_X_PX}px;
    row-gap: ${GAP_Y_PX}px;
    justify-content: start;
    align-content: start;
  }
  /* Cut helpers only on paper edges — aligned to each card edge. */
  .page-cut-guide {
    position: absolute;
    background: ${CUT_MARK};
    pointer-events: none;
    z-index: 5;
  }
  .page-cut-v {
    width: ${MARK_THICK_PX}px;
    height: ${EDGE_MARK_LEN_PX}px;
  }
  .page-cut-h {
    width: ${EDGE_MARK_LEN_PX}px;
    height: ${MARK_THICK_PX}px;
  }
  .card-cell {
    position: relative;
    width: ${CARD_WIDTH_PX}px;
    height: ${CARD_HEIGHT_PX}px;
  }
  .student-card {
    width: ${CARD_WIDTH_PX}px;
    height: ${CARD_HEIGHT_PX}px;
    border: ${MARK_THICK_PX}px solid ${LINE};
    border-radius: ${CARD_RADIUS_PX}px;
    padding: 8px 8px 8px;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    overflow: hidden;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .student-card.has-bus {
    background: var(--card-bus-bg, #e8f3ff);
    border-color: var(--card-bus-border, #b2ddff);
  }
  .student-card.no-bus {
    background: var(--card-no-bus-bg, #fff7ed);
    border-color: var(--card-no-bus-border, #fdcba7);
  }
  .card-head {
    width: 100%;
    display: flex;
    flex-direction: row;
    direction: ltr;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    margin: 0 0 6px;
    padding: 0 0 6px;
    border-bottom: 1px solid ${LINE};
  }
  .card-logos {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 3px;
    flex-shrink: 0;
  }
  .card-logos img {
    height: 18px;
    width: auto;
    max-width: 40px;
    object-fit: contain;
    display: block;
  }
  .card-school {
    flex: 1;
    min-width: 0;
    margin: 0;
    text-align: right;
    font-size: 9px;
    font-weight: 700;
    color: ${INK};
    line-height: 1.25;
    unicode-bidi: isolate;
  }
  .card-body {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-height: 0;
    padding-top: 10px;
  }
  .card-title {
    margin: 0;
    width: 100%;
    text-align: center;
    font-size: 9.5px;
    font-weight: 700;
    color: ${ACCENT};
    line-height: 1.2;
    unicode-bidi: isolate;
  }
  .card-name-ar,
  .card-name-en,
  .card-class {
    margin: 0;
    width: 100%;
    max-width: 100%;
    text-align: center;
    line-height: 1.25;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    unicode-bidi: isolate;
  }
  .card-name-ar {
    font-size: 13px;
    font-weight: 750;
    color: ${INK};
  }
  .card-name-en {
    font-size: 10px;
    font-weight: 600;
    color: ${MUTED};
  }
  .card-class {
    font-size: 10px;
    font-weight: 700;
    color: ${INK};
  }
  .card-bus {
    display: block;
    width: fit-content;
    max-width: 100%;
    margin: 2px auto 0;
    box-sizing: content-box;
    background: #eff8ff;
    color: #175cd3;
    border-radius: 999px;
    border-left: 10px solid #eff8ff;
    border-right: 10px solid #eff8ff;
    border-top: 4px solid #eff8ff;
    border-bottom: 4px solid #eff8ff;
    font-family: Arial, Helvetica, "Segoe UI", sans-serif;
    font-size: 11px;
    font-weight: 700;
    line-height: 13px;
    white-space: nowrap;
    text-align: center;
    direction: ltr;
  }
  .card-bus-pad {
    display: inline-block;
    width: 4px;
    height: 13px;
    vertical-align: middle;
  }
  .card-bus svg {
    width: 13px;
    height: 13px;
    display: inline-block;
    vertical-align: middle;
    margin-right: 6px;
  }
  .card-bus-label {
    display: inline-block;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 13px;
  }
  .card-qr {
    width: 108px;
    height: 108px;
    margin-top: auto;
    margin-bottom: 22px;
    padding: 4px;
    border: 1px solid ${LINE};
    border-radius: 8px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .card-qr img {
    width: 100px;
    height: 100px;
    display: block;
  }
  .card-qr-missing {
    color: ${MUTED};
    font-size: 8px;
    text-align: center;
    unicode-bidi: isolate;
  }
`;

@Injectable({ providedIn: 'root' })
export class StudentCardsPdfService {
  private logoDataUrl = '';
  private bannerDataUrl = '';

  async export(input: StudentCardPdfExportInput): Promise<void> {
    if (!input.students.length) {
      return;
    }

    await this.ensureLogos();

    const cards = await Promise.all(
      input.students.map(async student => ({
        ...student,
        qrDataUrl: student.qrCode
          ? await this.buildQrDataUrl(student.qrCode)
          : '',
      }))
    );

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageState = { started: false };

    for (let i = 0; i < cards.length; i += CARDS_PER_PAGE) {
      const pageCards = cards.slice(i, i + CARDS_PER_PAGE);
      const canvas = await this.rasterizeHtml(
        this.buildPageHtml(input, pageCards),
        input.isArabic,
      );
      this.addCanvasAsA4Page(canvas, pdf, pageState);
    }

    this.savePdf(pdf, `student-cards-${Date.now()}.pdf`);
  }

  private async buildQrDataUrl(qrCode: string): Promise<string> {
    try {
      return await QRCode.toDataURL(qrCode, {
        width: 280,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#173654', light: '#ffffff' },
      });
    } catch {
      return '';
    }
  }

  private buildPageHtml(
    input: StudentCardPdfExportInput,
    cards: Array<StudentCardPdfItem & { qrDataUrl: string }>,
  ): string {
    const busBg = normalizeHexColor(input.cardColors?.busBackground, DEFAULT_CARD_BUS_BG);
    const noBusBg = normalizeHexColor(input.cardColors?.noBusBackground, DEFAULT_CARD_NO_BUS_BG);
    const cardsHtml = cards.map(card => this.buildCardHtml(input, card)).join('');
    const edgeGuidesHtml = this.buildPageEdgeCutGuidesHtml(cards.length, input.cardColors?.cutMarginMm);
    // Keep sheet LTR for stable grid layout; isolate Arabic/English inside each field.
    return `
      <div id="sheet" dir="ltr" style="--card-bus-bg:${busBg};--card-bus-border:${darkenHex(busBg)};--card-no-bus-bg:${noBusBg};--card-no-bus-border:${darkenHex(noBusBg)};">
        <div class="cards-grid">
          ${cardsHtml}
        </div>
        ${edgeGuidesHtml}
      </div>`;
  }

  /**
   * Cut ticks only on the paper edges, aligned outside each card by cutMarginMm
   * so trimming leaves space around the card for cutting / lamination.
   */
  private buildPageEdgeCutGuidesHtml(cardCount: number, cutMarginMm?: number): string {
    const marks: string[] = [];
    const xEdges = new Set<number>();
    const yEdges = new Set<number>();
    const marginMm = Number.isFinite(Number(cutMarginMm))
      ? Math.min(20, Math.max(0, Number(cutMarginMm)))
      : DEFAULT_CUT_MARGIN_MM;
    const cutMarginPx = Math.round(marginMm * PX_PER_MM);

    for (let i = 0; i < cardCount && i < CARDS_PER_PAGE; i++) {
      const col = i % GRID_COLS;
      const row = Math.floor(i / GRID_COLS);
      const left = PAGE_MARGIN_PX + col * (CARD_WIDTH_PX + GAP_X_PX);
      const top = PAGE_MARGIN_PX + row * (CARD_HEIGHT_PX + GAP_Y_PX);
      xEdges.add(left - cutMarginPx);
      xEdges.add(left + CARD_WIDTH_PX + cutMarginPx);
      yEdges.add(top - cutMarginPx);
      yEdges.add(top + CARD_HEIGHT_PX + cutMarginPx);
    }

    for (const x of xEdges) {
      marks.push(
        `<span class="page-cut-guide page-cut-v" style="left:${x}px;top:${EDGE_MARK_INSET_PX}px;" aria-hidden="true"></span>`,
        `<span class="page-cut-guide page-cut-v" style="left:${x}px;bottom:${EDGE_MARK_INSET_PX}px;" aria-hidden="true"></span>`,
      );
    }

    for (const y of yEdges) {
      marks.push(
        `<span class="page-cut-guide page-cut-h" style="top:${y}px;left:${EDGE_MARK_INSET_PX}px;" aria-hidden="true"></span>`,
        `<span class="page-cut-guide page-cut-h" style="top:${y}px;right:${EDGE_MARK_INSET_PX}px;" aria-hidden="true"></span>`,
      );
    }

    return marks.join('');
  }

  private buildCardHtml(
    input: StudentCardPdfExportInput,
    card: StudentCardPdfItem & { qrDataUrl: string },
  ): string {
    const { labels } = input;
    const nameAr = (card.nameAr || '').trim();
    const nameEn = (card.nameEn || '').trim();
    const primaryName = nameAr || nameEn || '—';
    const secondaryName = nameAr && nameEn && nameEn !== nameAr ? nameEn : '';
    const classText = card.className
      ? `${labels.classLabel}: ${card.className}`
      : labels.noClass;
    const busText = (card.busText || '').trim();

    const busIcon = `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M4 16c0 .88.39 1.67 1 2.22V20a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h8v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm9 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM5 6h14v4H5V6z"/>
      </svg>`;

    const busBlock = busText
      ? `<div class="card-bus" dir="ltr"><span class="card-bus-pad"></span>${busIcon}<span class="card-bus-label">${this.escapeHtml(busText)}</span><span class="card-bus-pad"></span></div>`
      : '';
    const cardKindClass = busText ? 'has-bus' : 'no-bus';

    const qrBlock = card.qrDataUrl
      ? `<div class="card-qr"><img src="${card.qrDataUrl}" alt="QR"></div>`
      : `<div class="card-qr"><div class="card-qr-missing">${this.escapeHtml(labels.noQr)}</div></div>`;

    return `
      <div class="card-cell">
        <article class="student-card ${cardKindClass}">
          <div class="card-head">
            <div class="card-logos">
              <img src="${this.logoDataUrl}" alt="">
              <img src="${this.bannerDataUrl}" alt="">
            </div>
            <p class="card-school" dir="auto">${this.escapeHtml(input.schoolName || '')}</p>
          </div>
          <div class="card-body">
            <p class="card-title" dir="auto">${this.escapeHtml(labels.title)}</p>
            <h2 class="card-name-ar" dir="rtl" lang="ar">${this.escapeHtml(nameAr || primaryName)}</h2>
            ${secondaryName ? `<p class="card-name-en" dir="ltr" lang="en">${this.escapeHtml(secondaryName)}</p>` : ''}
            <p class="card-class" dir="auto">${this.escapeHtml(classText)}</p>
            ${busBlock}
            ${qrBlock}
          </div>
        </article>
      </div>`;
  }

  private resolveAssetUrl(relativePath: string): string {
    return new URL(relativePath, document.baseURI).href;
  }

  private async toDataUrl(src: string): Promise<string> {
    const response = await fetch(src);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Failed to read image: ${src}`));
      reader.readAsDataURL(blob);
    });
  }

  private async ensureLogos(): Promise<void> {
    if (this.logoDataUrl && this.bannerDataUrl) {
      return;
    }
    const [logo, banner] = await Promise.all([
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/school-logo.png')),
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/year-banner.jpg')),
    ]);
    this.logoDataUrl = logo;
    this.bannerDataUrl = banner;
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private wrapDocument(innerHtml: string, _isArabic: boolean): string {
    return `<!DOCTYPE html>
<html lang="ar" dir="ltr">
<head>
  <meta charset="utf-8">
  <link rel="stylesheet" href="/assets/fonts/fonts.css">
  <style>${SHEET_CSS}</style>
</head>
<body>${innerHtml}</body>
</html>`;
  }

  private async waitFrames(targetWindow: Window = window): Promise<void> {
    const fonts = targetWindow.document?.fonts;
    if (fonts) {
      try {
        await fonts.load('400 13px "Noto Naskh Arabic"');
        await fonts.load('700 13px "Noto Naskh Arabic"');
        await fonts.ready;
      } catch {
        // Capture still runs if the face is unavailable.
      }
    }
    await new Promise<void>(resolve => {
      targetWindow.requestAnimationFrame(() => {
        targetWindow.requestAnimationFrame(() => resolve());
      });
    });
  }

  private async rasterizeHtml(innerHtml: string, isArabic: boolean): Promise<HTMLCanvasElement> {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '0';
    iframe.style.top = '0';
    iframe.style.width = `${PAGE_WIDTH_PX}px`;
    iframe.style.height = '240px';
    iframe.style.opacity = '0.01';
    iframe.style.pointerEvents = 'none';
    iframe.style.border = '0';
    iframe.style.zIndex = '-1';
    iframe.style.background = '#ffffff';

    const loaded = new Promise<void>(resolve => {
      iframe.onload = () => resolve();
    });
    iframe.srcdoc = this.wrapDocument(innerHtml, isArabic);
    document.body.appendChild(iframe);
    await loaded;

    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        throw new Error('Failed to prepare PDF page');
      }
      if (doc.fonts?.ready) {
        await doc.fonts.ready;
      }
      await this.waitFrames(win);

      const sheet = doc.getElementById('sheet');
      if (!sheet) {
        throw new Error('Failed to build PDF sheet');
      }

      iframe.style.height = `${PAGE_HEIGHT_PX + 48}px`;
      await this.waitFrames(win);

      return await toCanvas(sheet, {
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        cacheBust: true,
        skipFonts: true,
        width: PAGE_WIDTH_PX,
        height: PAGE_HEIGHT_PX,
        style: {
          fontFamily: ARABIC_FONT,
          color: INK,
          background: '#ffffff',
          width: `${PAGE_WIDTH_PX}px`,
          height: `${PAGE_HEIGHT_PX}px`,
        },
      });
    } finally {
      iframe.remove();
    }
  }

  private addCanvasAsA4Page(
    canvas: HTMLCanvasElement,
    pdf: jsPDF,
    pageState: { started: boolean },
  ): void {
    if (pageState.started) {
      pdf.addPage();
    }
    // Full-bleed A4 so HTML millimetres match printed millimetres (exact ID-1 cards).
    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      0,
      0,
      A4_WIDTH_MM,
      A4_HEIGHT_MM,
      undefined,
      'NONE',
    );
    pageState.started = true;
  }

  private savePdf(pdf: jsPDF, filename: string): void {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => {
      try {
        window.open(url, '_blank');
      } catch {
        /* ignore */
      }
    }, 250);
  }
}
